// src/composables/usePocketMemberProfile.js
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePocketAuthStore } from '@/stores/usePocketAuthStore'
import usePocketbase from './usePocketbase'

export function useMemberProfile() {
  const pb = usePocketbase()
  const auth = usePocketAuthStore()

  const loading = ref(false)
  const error = ref(null)

  const interests = ref([])
  const languages = ref([])
  const types = ref([])

  const { locale } = useI18n()

  const localizeOptions = (records) =>
    records.map((r) => ({
      id: r.id,
      label: r.values?.[locale.value] || r.label || r.id,
    }))

  const fetchCurrentMember = async () => {
    loading.value = true
    error.value = null

    try {
      const userId = pb.authStore.model?.id
      if (!userId) throw new Error('Not logged in')

      const result = await pb
        .collection('members')
        .getFirstListItem(`user="${userId}"`, {
          expand: 'language,type,interest',
        })

      // Normalize relation IDs for use in selects
      result.language = result.language || result.expand?.language?.id || ''
      result.type = result.type || result.expand?.type?.id || ''
      result.interest = result.interest || result.expand?.interest?.id || ''

      auth.member = result
    } catch (err) {
      error.value = err.message
    } finally {
      loading.value = false
    }

    // Fetch and localize relation collections
    try {
      const [langRecords, typeRecords, interestRecords] = await Promise.all([
        pb.collection('language').getFullList(),
        pb.collection('entity').getFullList(),
        pb.collection('interest').getFullList(),
      ])
      languages.value = localizeOptions(langRecords)
      types.value = localizeOptions(typeRecords)
      interests.value = localizeOptions(interestRecords)
    } catch (err) {
      console.warn('Failed to load option lists:', err.message)
    }
  }

  watch(locale, fetchCurrentMember)

  const updateMember = async (data) => {
    if (!auth.member) throw new Error('No member loaded')
    const updated = await pb.collection('members').update(auth.member.id, data)
    auth.member = updated
    return updated
  }

  return {
    member: auth.member,
    loading,
    error,
    languages,
    types,
    interests,
    fetchCurrentMember,
    updateMember,
  }
}
